import { log } from "../../shared/services/debug-logger";
import { fetchDataWithParams, postData } from "../../shared/services/service";
import {
  CancelHullCleaningRequestInput,
  CancelHullCleaningRequestResponse,
  ConfirmHullCleaningBookingInput,
  ConfirmHullCleaningBookingResponse,
  CreateHullCleaningRequestInput,
  CreateHullCleaningRequestResponse,
  HullCleaningEvent,
  HullCleaningRequest,
  HullCleaningRequestBundle,
  HullCleaningVendor,
  HullCleaningVendorsResponse,
  SubmitHullCleaningOutcomeInput,
  SubmitHullCleaningOutcomeResponse,
  SubmitHullCleaningPlanningInput,
  SubmitHullCleaningPlanningResponse,
  UpdateHullCleaningRequestInput,
  UpdateHullCleaningRequestResponse,
} from "../models/hull-cleaning.models";
import axios from "axios";

export async function createHullCleaningRequest(
  payload: CreateHullCleaningRequestInput,
): Promise<CreateHullCleaningRequestResponse> {
  log("SERV - createHullCleaningRequest");
  const result = await postData("hull-cleaning/requests", payload);
  log("SERV - createHullCleaningRequest - response");
  return result.data as CreateHullCleaningRequestResponse;
}

export async function getHullCleaningRequestBundle(requestId: string): Promise<HullCleaningRequestBundle> {
  const result = await fetchDataWithParams(`hull-cleaning/requests/${encodeURIComponent(requestId)}`);
  return result.data as HullCleaningRequestBundle;
}

export async function getActiveHullCleaningRequestBundleByVessel(
  imoNumber: string,
  vesselCode: string,
): Promise<HullCleaningRequestBundle | null> {
  try {
    const result = await fetchDataWithParams(
      `hull-cleaning/requests/${encodeURIComponent(imoNumber)}/${encodeURIComponent(vesselCode)}/active`,
    );
    return (result.data as HullCleaningRequestBundle) ?? null;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    throw error;
  }
}

export async function getCompletedHullCleaningRequestsByVessel(
  imoNumber: string,
  vesselCode: string,
): Promise<HullCleaningRequest[]> {
  try {
    const result = await fetchDataWithParams(
      `hull-cleaning/vessels/${encodeURIComponent(imoNumber)}/${encodeURIComponent(vesselCode)}/completed`,
    );
    return (result.data as HullCleaningRequest[]) ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return [];
    }

    throw error;
  }
}

export async function getCompletedHullCleaningEventsByVessel(
  imoNumber: string,
  vesselCode: string,
): Promise<HullCleaningEvent[]> {
  try {
    const result = await fetchDataWithParams(
      `hull-cleaning/vessels/${encodeURIComponent(imoNumber)}/${encodeURIComponent(vesselCode)}/completed-events`,
    );
    return (result.data as HullCleaningEvent[]) ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return [];
    }

    throw error;
  }
}

export async function getPreferredHullCleaningRequestBundleByVessel(
  imoNumber: string,
  vesselCode: string,
): Promise<HullCleaningRequestBundle | null> {
  const activeBundle = await getActiveHullCleaningRequestBundleByVessel(imoNumber, vesselCode);
  log("SERV - getPreferredHullCleaningRequestBundleByVessel", { active: Boolean(activeBundle) });
  if (activeBundle) {
    return activeBundle;
  }

  const completedRequests = await getCompletedHullCleaningRequestsByVessel(imoNumber, vesselCode);
  const latestCompletedRequest = completedRequests[0];
  if (!latestCompletedRequest?.requestId) {
    return null;
  }

  return getHullCleaningRequestBundle(latestCompletedRequest.requestId);
}

export async function getLatestCompletedHullCleaningRequestBundleByVessel(
  imoNumber: string,
  vesselCode: string,
): Promise<HullCleaningRequestBundle | null> {
  const completedRequests = await getCompletedHullCleaningRequestsByVessel(imoNumber, vesselCode);
  const latestCompletedRequest = completedRequests[0];
  if (!latestCompletedRequest?.requestId) {
    return null;
  }

  return getHullCleaningRequestBundle(latestCompletedRequest.requestId);
}

export async function getHullCleaningVendors(): Promise<HullCleaningVendor[]> {
  const result = await fetchDataWithParams("hull-cleaning/vendors");
  return ((result.data as HullCleaningVendorsResponse).vendors ?? []).filter(
    (vendor) => vendor.company.trim().length > 0,
  );
}

export async function submitHullCleaningPlanning(
  payload: SubmitHullCleaningPlanningInput,
): Promise<SubmitHullCleaningPlanningResponse> {
  const result = await postData(`hull-cleaning/requests/${encodeURIComponent(payload.requestId)}/planning`, {
    imoNumber: payload.imoNumber,
    vesselCode: payload.vesselCode,
    opsCoordinatorName: payload.opsCoordinatorName,
    cleaningDate: payload.cleaningDate,
    cleaningPort: payload.cleaningPort,
    bookingComment: payload.bookingComment,
    updatedBy: payload.updatedBy,
  });
  log("SERV - submitHullCleaningPlanning - response");
  return result.data as SubmitHullCleaningPlanningResponse;
}

export async function confirmHullCleaningBooking(
  payload: ConfirmHullCleaningBookingInput,
): Promise<ConfirmHullCleaningBookingResponse> {
  const result = await postData(
    `hull-cleaning/requests/${encodeURIComponent(payload.requestId)}/booking-confirmation`,
    {
      imoNumber: payload.imoNumber,
      vesselCode: payload.vesselCode,
      updatedBy: payload.updatedBy,
    },
  );
  log("SERV - confirmHullCleaningBooking - response", {
    emailSent: (result.data as ConfirmHullCleaningBookingResponse).emailSent,
  });
  return result.data as ConfirmHullCleaningBookingResponse;
}

export async function submitHullCleaningOutcome(
  payload: SubmitHullCleaningOutcomeInput,
): Promise<SubmitHullCleaningOutcomeResponse> {
  const result = await postData(`hull-cleaning/requests/${encodeURIComponent(payload.requestId)}/outcome`, {
    imoNumber: payload.imoNumber,
    vesselCode: payload.vesselCode,
    opsCoordinatorName: payload.opsCoordinatorName,
    cleaningDate: payload.cleaningDate,
    cleaningPort: payload.cleaningPort,
    vendor: payload.vendor,
    vendorRef: payload.vendorRef,
    comments: payload.comments,
    outcomes: payload.outcomes,
    hullCleaningType: payload.hullCleaningType,
    updatedBy: payload.updatedBy,
  });
  log("SERV - submitHullCleaningOutcome - response");
  return result.data as SubmitHullCleaningOutcomeResponse;
}

export async function updateHullCleaningRequest(
  payload: UpdateHullCleaningRequestInput,
): Promise<UpdateHullCleaningRequestResponse> {
  const result = await postData(`hull-cleaning/requests/${encodeURIComponent(payload.requestId)}/update`, {
    imoNumber: payload.imoNumber,
    vesselCode: payload.vesselCode,
    opsCoordinatorName: payload.opsCoordinatorName,
    cleaningDate: payload.cleaningDate,
    cleaningPort: payload.cleaningPort,
    bookingComment: payload.bookingComment,
    vendor: payload.vendor,
    vendorRef: payload.vendorRef,
    outcomes: payload.outcomes,
    updatedBy: payload.updatedBy,
    updatedAt: payload.updatedAt,
  });
  log("SERV - updateHullCleaningRequest - response");
  return result.data as UpdateHullCleaningRequestResponse;
}

export async function cancelHullCleaningRequest(
  payload: CancelHullCleaningRequestInput,
): Promise<CancelHullCleaningRequestResponse> {
  const result = await postData(`hull-cleaning/requests/${encodeURIComponent(payload.requestId)}/cancel`, {
    imoNumber: payload.imoNumber,
    vesselCode: payload.vesselCode,
    cancelComment: payload.cancelComment,
    updatedBy: payload.updatedBy,
  });
  log("SERV - cancelHullCleaningRequest - response");
  return result.data as CancelHullCleaningRequestResponse;
}
